import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Avatar,
  Tooltip,
  CircularProgress,
  TextField,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  NewUserRow,
  SubmitResult,
} from "../../../../../../../redux/slices/users/managerCreation";
import { reviewModalStyles as sx } from "./ReviewModal.styles";

const k = (key: string) => `Users_managers.table_v2.${key}`;

const statusColors: Record<
  NewUserRow["status"],
  "default" | "success" | "warning" | "error"
> = {
  vide: "default",
  valid: "success",
  existing: "warning",
  error: "error",
};

const errorMessageKey: Partial<
  Record<NonNullable<NewUserRow["errorType"]>, string>
> = {
  duplicate: "error_duplicate",
  already_exists: "error_already_exists",
  missing_role: "error_missing_role",
  invalid_format: "status_error",
};

const hasIssue = (row: NewUserRow) =>
  row.status === "error" || row.status === "existing";

// ─── Review row card (pre-submit) ────────────────────────────────────────────

interface RowCardProps {
  row: NewUserRow;
  index: number;
  onGoToRow: (id: string) => void;
}

const RowCard: FC<RowCardProps> = ({ row, index, onGoToRow }) => {
  const [t] = useTranslation("global");
  const issue = hasIssue(row);

  const statusMap: Record<NewUserRow["status"], string> = {
    vide: t(k("status_empty")),
    valid: t(k("status_valid")),
    existing: t(k("status_existing")),
    error: t(k("status_error")),
  };

  return (
    <Tooltip
      title={issue ? t(k("click_to_fix")) : ""}
      placement="top-start"
      arrow
    >
      <Box
        onClick={issue ? () => onGoToRow(row.id) : undefined}
        sx={sx.rowCard(issue)}
      >
        <Avatar sx={sx.rowAvatar(issue)}>{index + 1}</Avatar>

        <Box flex={1} display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailOutlinedIcon sx={sx.emailIcon} />
            <Typography variant="body2" fontWeight={600}>
              {row.email || "—"}
            </Typography>
            <Chip
              label={statusMap[row.status]}
              size="small"
              color={statusColors[row.status]}
              variant="outlined"
              sx={sx.statusChip}
            />
          </Box>

          {issue && row.errorType && errorMessageKey[row.errorType] && (
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{
                color:
                  row.status === "existing" ? "warning.main" : "error.main",
              }}
            >
              <WarningAmberOutlinedIcon sx={sx.warningIcon} />
              <Typography variant="caption">
                {t(k(errorMessageKey[row.errorType]!))}
              </Typography>
            </Box>
          )}

          <Box display="flex" flexWrap="wrap" gap={1.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <WorkOutlineIcon sx={sx.detailIcon} />
              <Typography variant="caption" color="text.secondary">
                {t(k("col_role"))}:
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {row.roleName || "—"}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              <LocationOnOutlinedIcon sx={sx.detailIcon} />
              <Typography variant="caption" color="text.secondary">
                {t(k("col_site"))}:
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {row.siteName || "—"}
              </Typography>
            </Box>

            {(row.assignedGroups?.length ?? 0) > 0 && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <GroupsOutlinedIcon sx={sx.detailIcon} />
                <Typography variant="caption" color="text.secondary">
                  {t(k("col_groups"))}:
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {row.assignedGroups.length}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {issue && <ArrowForwardIosIcon sx={sx.arrowIcon} />}
      </Box>
    </Tooltip>
  );
};

// ─── Submit result card (post-submit) ────────────────────────────────────────

interface SubmitResultCardProps {
  result: SubmitResult;
  currentEmail: string;
  onChangeEmail: (rowId: string, value: string) => void;
}

const SubmitResultCard: FC<SubmitResultCardProps> = ({
  result,
  currentEmail,
  onChangeEmail,
}) => {
  const [t] = useTranslation("global");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: result.success ? "success.light" : "warning.main",
        bgcolor: result.success
          ? "rgba(46, 125, 50, 0.04)"
          : "rgba(255, 152, 0, 0.05)",
      }}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {result.success ? (
          <CheckCircleOutlineIcon
            sx={{ fontSize: 15, color: "success.main", flexShrink: 0 }}
          />
        ) : (
          <WarningAmberOutlinedIcon
            sx={{ fontSize: 15, color: "warning.main", flexShrink: 0 }}
          />
        )}
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ flex: 1, minWidth: 0, wordBreak: "break-all" }}
        >
          {result.email}
        </Typography>
        <Chip
          label={
            result.success
              ? t(k("result_sent"))
              : (result.errorMessage ?? t(k("result_error")))
          }
          size="small"
          color={result.success ? "success" : "warning"}
          variant="outlined"
          sx={{ fontSize: "0.7rem", height: 20, flexShrink: 0 }}
        />
      </Box>

      {!result.success && (
        <TextField
          size="small"
          fullWidth
          label={t(k("update_email_label"))}
          value={currentEmail}
          onChange={(e) => onChangeEmail(result.rowId, e.target.value)}
          inputProps={{ type: "email" }}
        />
      )}
    </Box>
  );
};

// ─── Main modal ──────────────────────────────────────────────────────────────

interface ReviewModalProps {
  open: boolean;
  rows: NewUserRow[];
  onClose: () => void;
  onGoToRow: (id: string) => void;
  onSend: () => void;
  submitLoading: boolean;
  submitSuccess: boolean;
  submitResults: SubmitResult[];
  onChangeEmail: (rowId: string, value: string) => void;
}

const ReviewModal: FC<ReviewModalProps> = ({
  open,
  rows,
  onClose,
  onGoToRow,
  onSend,
  submitLoading,
  submitSuccess,
  submitResults,
  onChangeEmail,
}) => {
  const [t] = useTranslation("global");

  const visibleRows = rows.filter((r) => r.email !== "");
  const hasBlockingErrors = rows.some((r) => hasIssue(r));
  const successCount = submitResults.filter((r) => r.success).length;
  const failCount = submitResults.filter((r) => !r.success).length;

  return (
    <Dialog
      open={open}
      onClose={!submitLoading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: sx.dialogPaper }}
    >
      {/* ── Loading ── */}
      {submitLoading && (
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={6}
            gap={2}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              {t(k("sending_invitations"))}
            </Typography>
          </Box>
        </DialogContent>
      )}

      {/* ── Results ── */}
      {!submitLoading && submitSuccess && submitResults.length > 0 && (
        <>
          <DialogTitle sx={{ pb: 0.5 }}>
            <Typography variant="h6" fontWeight={700}>
              {t(k("results_title"))}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {successCount > 0 && (
                <Box component="span" sx={{ color: "success.main" }}>
                  {successCount} {t(k("results_sent"))}
                </Box>
              )}
              {successCount > 0 && failCount > 0 && " · "}
              {failCount > 0 && (
                <Box component="span" sx={{ color: "warning.main" }}>
                  {failCount} {t(k("results_failed"))}
                </Box>
              )}
            </Typography>
          </DialogTitle>

          <Divider sx={{ mt: 2 }} />

          <DialogContent sx={sx.dialogContent}>
            {submitResults.map((result) => {
              const currentRow = rows.find((r) => r.id === result.rowId);
              return (
                <SubmitResultCard
                  key={result.rowId}
                  result={result}
                  currentEmail={currentRow?.email ?? result.email}
                  onChangeEmail={onChangeEmail}
                />
              );
            })}
          </DialogContent>

          <Divider />

          <DialogActions sx={sx.dialogActions}>
            <Box display="flex" gap={1} width="100%">
              <Button
                variant="contained"
                onClick={onClose}
                sx={sx.actionButton}
              >
                {t(k("close_button"))}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {/* ── Review (default) ── */}
      {!submitLoading && !submitSuccess && (
        <>
          <DialogTitle sx={{ pb: 0.5 }}>
            <Typography variant="h6" fontWeight={700}>
              {t(k("review_title"))}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t(k("review_subtitle"))}
            </Typography>
          </DialogTitle>

          <Divider sx={{ mt: 2 }} />

          <DialogContent sx={sx.dialogContent}>
            {visibleRows.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={3}
              >
                —
              </Typography>
            ) : (
              visibleRows.map((row, i) => (
                <RowCard
                  key={row.id}
                  row={row}
                  index={i}
                  onGoToRow={onGoToRow}
                />
              ))
            )}
          </DialogContent>

          <Divider />

          <DialogActions sx={sx.dialogActions}>
            {hasBlockingErrors && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                sx={sx.sendBlockedBox}
              >
                <WarningAmberOutlinedIcon sx={sx.sendBlockedIcon} />
                <Typography variant="caption">
                  {t(k("send_blocked"))}
                </Typography>
              </Box>
            )}
            <Box display="flex" gap={1} width="100%">
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={onClose}
                sx={sx.actionButton}
              >
                {t(k("continue_editing"))}
              </Button>
              <Button
                variant="contained"
                startIcon={<SendOutlinedIcon />}
                onClick={onSend}
                disabled={visibleRows.length === 0 || hasBlockingErrors}
                sx={sx.actionButton}
              >
                {t(k("send_invitations"))}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ReviewModal;
